import React from 'react';
import Uppy from '@uppy/core';
import type { Meta, Body } from '@uppy/core';
import Dashboard, { DashboardOptions } from '@uppy/dashboard';
import Box from '@uppy/box';
import Dropbox from '@uppy/dropbox';
import GoogleDrive from '@uppy/google-drive';
import Tus from '@uppy/tus';
import Url from '@uppy/url';
import ImageEditor from '@uppy/image-editor';
import DragDrop from '@uppy/drag-drop';
import { FC, useEffect, useRef, useCallback } from 'react';
import { type UppyUploaderProps } from '@components/common/uppy/types/UppyTypes';
import { delay } from '@api/ApiHelper';
import { sendAction } from '@helpers/datadog-rum';
import {
  transformUppyUCSResult,
  getValidIdentityToken,
  COMPANION_SANDBOX_URL,
  COMPANION_ALLOWED_HOSTS,
  INVALID_FILENAMES_SET,
  UPLOAD_OVERHEAD_PER_FILE_MS,
  UPLOAD_BASELINE_BYTES_PER_MS,
} from '@helpers/uppy';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import '@uppy/image-editor/dist/style.min.css';
import '@uppy/drag-drop/dist/style.css';

import '@components/common/uppy/styles/uppy_uploader.scss';
import type { AssemblyResult as Result } from '@mocks/uppy-transloadit';
import type { Locale } from '@uppy/utils/lib/Translator';
import FileSearchPlugin from '../plugins/Search';
import { finishUploadSession, getUnqueuedFileIDs, hasUploadsInProgress } from '../uploadSession';
import { RemoteAwareQueue } from './RemoteAwareQueue';

// Uppy's PluginTarget type does not accept a generic plugin class directly, but the
// runtime value is the Dashboard plugin, which is what `.use({ target })` expects.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DASHBOARD_TARGET = Dashboard as any;

const TUS_CHUNK_SIZE = 6 * 1024 * 1024;
const LOCAL_UPLOAD_LIMIT = 4;
const REMOTE_UPLOAD_LIMIT = 10;

interface BrandfolderTusOptions {
  bearerToken?: string;
  isFl2?: boolean;
  refreshToken?: string;
  version?: string;
  tusUrl?: string;
}

interface ErrorArray {
  error: string[];
}

interface ErrorObject {
  error: string;
}

interface FetchTusSignedUrlOptionsV2 extends Pick<BrandfolderTusOptions, 'bearerToken'> {
  tusUrl: string;
}

interface TusSignedURLsResponse {
  signed_url: string;
}

export const fetchTusSignedUrlV2 = async (options: FetchTusSignedUrlOptionsV2): Promise<string> => {
  try {
    const { bearerToken, tusUrl } = options;

    const response = await fetch(`${tusUrl}/signed_url`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${bearerToken}`
      },
      method: 'GET'
    });

    if (response.ok) {
      const signedUrls = (await response.json()) as TusSignedURLsResponse;
      return signedUrls?.signed_url || '';
    } else {
      const e = (await response.json()) as ErrorArray | ErrorObject;
      throw new Error(Array.isArray(e?.error) ? e.error[0] : e?.error || 'Unknown error');
    }
  } catch (error) {
    console.error('fetchTusSignedUrlV2', { error });
    return '';
  }
};

export const SIGNED_URL_MAX_ATTEMPTS = 3;
export const SIGNED_URL_RETRY_DELAY_MS = 500;

/**
 * Fetches the signed URL, retrying empty responses with a linear backoff.
 * @returns the signed URL, or '' when every attempt fails.
 */
export const fetchTusSignedUrlWithRetry = async (options: FetchTusSignedUrlOptionsV2): Promise<string> => {
  for (let attempt = 1; attempt <= SIGNED_URL_MAX_ATTEMPTS; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const signedUrl = await fetchTusSignedUrlV2(options);
    if (signedUrl) return signedUrl;
    if (attempt < SIGNED_URL_MAX_ATTEMPTS) {
      // eslint-disable-next-line no-await-in-loop
      await delay(SIGNED_URL_RETRY_DELAY_MS * attempt);
    }
  }
  return '';
};

export const UppyUcsUploader: FC<UppyUploaderProps> = ({
  handleUpload,
  uniqIdentifier,
  button = false,
  closeOnComplete = false,
  locale = {
    strings: {
      dropPaste: 'Drag file(s) here or %{browse}',
      dropHereOr: 'Drag file(s) here or %{browse}',
      browse: 'click to browse'
    }
  },
  restrictions = {},
  cropperOptions,
  endpoint,
  template = 'file_ingest',
  trigger = undefined,
  onTriggerClickHandler,
  ucsSdkRef,
  reinitializeSdk,
  inline = false,
  guestUpload,
}) => {
  // One instance per mount. Callers pass `restrictions`/`locale` as inline object
  // literals, so their identity changes on every parent render; using them as
  // `useMemo` deps rebuilt Uppy on each keystroke and left the new instance
  // without the plugins the mount effect installed on the original one.
  const uppyRef = useRef<Uppy<Meta, Body> | null>(null);
  if (uppyRef.current === null) {
    uppyRef.current = new Uppy<Meta, Body>({
      allowMultipleUploadBatches: true,
      id: `${uniqIdentifier}-uppy`,
      restrictions: { maxNumberOfFiles: 3000, ...restrictions },
      locale: locale as unknown as Locale,
      onBeforeFileAdded: (currentFile) => !INVALID_FILENAMES_SET.has(currentFile?.name),
    });
  }
  const uppy = uppyRef.current;

  // Keep the newest callback reachable from the mount-only effect below.
  const handleUploadRef = useRef(handleUpload);
  handleUploadRef.current = handleUpload;

  // `closeOnComplete` tears the instance down early; make sure unmount does not
  // destroy it a second time.
  const isDestroyedRef = useRef(false);
  const destroyUppy = (): void => {
    if (isDestroyedRef.current) return;
    isDestroyedRef.current = true;
    uppy.destroy();
  };

  // Restrictions/locale still need to track prop changes, but by updating the
  // existing instance rather than replacing it. Compare by value, since the
  // objects are recreated on every render.
  const restrictionsKey = JSON.stringify(restrictions);
  const localeKey = JSON.stringify(locale);
  useEffect(() => {
    uppy.setOptions({
      restrictions: { maxNumberOfFiles: 3000, ...restrictions },
      locale: locale as unknown as Locale,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictionsKey, localeKey]);

  // Create handler function that can be passed to parent components
  const handleTriggerClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const dashboard = uppy.getPlugin<Dashboard<Meta, Body>>('Dashboard');
    if (dashboard) {
      dashboard.openModal();
    }
  }, [uppy]);

  // Directly assign handler to ref if provided (no useEffect needed)
  // Refs are mutable and can be assigned during render without causing issues
  if (onTriggerClickHandler) {
    onTriggerClickHandler.current = handleTriggerClick;
  }

  // Cleanup: clear ref on unmount
  useEffect(() => {
    return (): void => {
      if (onTriggerClickHandler) {
        onTriggerClickHandler.current = null;
      }
    };
  }, [onTriggerClickHandler]);

  const templateName = template.replace(/-/g, '_');
  const dashboardOptions: DashboardOptions<Meta, Body> = {
    browserBackButtonClose: button,
    // Dashboard would close after the first finished batch; `finishUploadSession` closes it once all are done
    closeAfterFinish: false,
    closeModalOnClickOutside: button,
    inline,
    proudlyDisplayPoweredByUppy: false,
    showProgressDetails: true,
    trigger,
    // Only set target for inline mode, and make sure it's valid
    ...(inline && { target: `#${uniqIdentifier}-dashboard` }),
    width: inline ? '100%' : 10,
    showSelectedFiles: true,
    showRemoveButtonAfterComplete: true,
    // Ensure it doesn't auto-open file picker
    note: 'Select files from your computer or connect to external services'
  };

  const handleError = (file, error): void => {
    sendAction('Error Uploading File to Tus', {
      tags: {},
      extra: {
        file_name: file?.name ?? null,
        file_size: file?.size ?? null,
        file_type: file?.type ?? null,
        http_status: error?.xhr?.status ?? error?.status ?? null,
        error_message: error?.message ?? null,
      }
    });
  };

  useEffect(() => {
    // Track upload session duration and total size for Datadog performance monitoring
    let uploadStartTime: number | null = null;
    let batchTotalBytes = 0;
    let sessionSuccessCount = 0;
    let sessionFailedCount = 0;
    // `complete` handlers awaiting signed URLs; their batch has already left Uppy's `currentUploads`
    let pendingCompletions = 0;
    let lastProgressTime: number | null = null;
    let stuckCheckInterval: ReturnType<typeof setInterval> | null = null;

    const STUCK_NO_PROGRESS_MS = 10 * 60 * 1000;
    const STUCK_CHECK_INTERVAL_MS = 60 * 1000;

    const clearStuckCheck = (): void => {
      if (stuckCheckInterval !== null) {
        clearInterval(stuckCheckInterval);
        stuckCheckInterval = null;
      }
    };

    const resetSessionMetrics = (): void => {
      uploadStartTime = null;
      batchTotalBytes = 0;
      sessionSuccessCount = 0;
      sessionFailedCount = 0;
    };

    const sendSessionMetrics = (): void => {
      if (uploadStartTime === null) return;

      const duration_ms = Date.now() - uploadStartTime;
      const file_count = sessionSuccessCount + sessionFailedCount;
      const expected_duration_ms =
        file_count * UPLOAD_OVERHEAD_PER_FILE_MS +
        Math.round(batchTotalBytes / UPLOAD_BASELINE_BYTES_PER_MS);
      // No counted files and ~0 bytes leave no baseline. Dividing would send Infinity/NaN.
      const duration_ratio = expected_duration_ms > 0
        ? Math.round((duration_ms / expected_duration_ms) * 100)
        : undefined;
      sendAction('Uppy UCS Upload Completed', {
        duration_ms,
        total_size_bytes: batchTotalBytes,
        file_count,
        success_count: sessionSuccessCount,
        failed_count: sessionFailedCount,
        expected_duration_ms,
        ...(duration_ratio !== undefined && { duration_ratio }),
        brandfolder_id: window.BFG?.brandfolder_key,
        user_id: window.BFG?.currentUser?.user_id,
      });
    };

    const onUploadStart = (_uploadID, files): void => {
      // Accumulate across batches in case user adds files mid-upload
      if (uploadStartTime === null) uploadStartTime = Date.now();
      batchTotalBytes += files.reduce((sum, file) => sum + (file?.size ?? 0), 0);
      lastProgressTime = Date.now();
      clearStuckCheck();
      stuckCheckInterval = setInterval(() => {
        if (lastProgressTime !== null && Date.now() - lastProgressTime >= STUCK_NO_PROGRESS_MS) {
          sendAction('Uppy Upload Stuck - No Progress', {
            tags: {},
            extra: {
              minutes_since_progress: Math.round((Date.now() - lastProgressTime) / 60000),
              total_size_bytes: batchTotalBytes,
            }
          });
          clearStuckCheck(); // fire once only
        }
      }, STUCK_CHECK_INTERVAL_MS);
    };

    const onUploadProgress = (): void => {
      lastProgressTime = Date.now();
    };

    const onCancelAll = (): void => {
      // Reset so a subsequent upload starts fresh
      resetSessionMetrics();
      lastProgressTime = null;
      clearStuckCheck();
    };

    const onTransloaditComplete = (assembly): void => {
      const files = assembly.results[':original'] || assembly.results[templateName];
      handleUploadRef.current(files);
    };

    // eslint-disable-next-line
    const onComplete = async (result): Promise<void> => {
      pendingCompletions += 1;
      let transformedFiles: unknown;
      try {
        // Get identity token once for all files
        const identityToken = await getValidIdentityToken(ucsSdkRef, reinitializeSdk);

        // Fetch signed URLs using the final assembled TUS URL. Files that never get one can't be processed
        // downstream, so they resolve to null and are dropped. `fetchTusSignedUrlWithRetry` never rejects.
        const signedFiles = await Promise.all(
          (result?.successful || []).map(async (file) => {
            const tusUrl = file?.response?.uploadURL || '';
            // An empty upload URL would request `/signed_url` on this origin, then retry it.
            if (!tusUrl) {
              console.error('Missing TUS upload URL', { fileName: file?.name });
              return null;
            }
            const brandfolderTusUrl = await fetchTusSignedUrlWithRetry({
              bearerToken: identityToken,
              tusUrl,
            });
            if (!brandfolderTusUrl) {
              console.error(`Failed to fetch signed URL after ${SIGNED_URL_MAX_ATTEMPTS} attempts`, {
                fileName: file?.name,
                tusUrl,
              });
              sendAction('Uppy Signed URL Fetch Failed', {
                tags: {},
                extra: { fileName: file?.name, tusUrl }
              });
              return null;
            }
            file.meta = { ...file.meta, brandfolderTusUrl };
            return file;
          }),
        );

        if (result) {
          result.successful = signedFiles.filter(
            (file): file is NonNullable<typeof file> => file !== null,
          );
        }

        // Transform result data to desired format using helper function
        transformedFiles = await transformUppyUCSResult(result);
      } finally {
        pendingCompletions -= 1;
      }

      sessionSuccessCount += result?.successful?.length ?? 0;
      sessionFailedCount += result?.failed?.length ?? 0;

      // Files added mid-upload that were never started keep the session (and modal) open so they can still upload.
      // Tell the parent this batch is not the whole session: some callers unmount on `handleUpload`.
      if (
        isDestroyedRef.current
        || pendingCompletions > 0
        || hasUploadsInProgress(uppy)
        || getUnqueuedFileIDs(uppy).length > 0
      ) {
        handleUploadRef.current(transformedFiles as Result[], { sessionComplete: false });
        return;
      }

      sendSessionMetrics();
      resetSessionMetrics();

      // Reset before notifying the parent: `handleUpload` normally flips parent
      // state that unmounts this component, so anything after it may not run.
      finishUploadSession(uppy, {
        closeModal: button && !inline,
        destroy: closeOnComplete ? destroyUppy : undefined,
      });

      handleUploadRef.current(transformedFiles as Result[], { sessionComplete: true });
    };

    uppy
      .use(Tus, {
        endpoint: `https://${endpoint}/v2/files/`,
        rateLimitedQueue: new RemoteAwareQueue(LOCAL_UPLOAD_LIMIT, REMOTE_UPLOAD_LIMIT),
        parallelUploads: 3,
        removeFingerprintOnSuccess: true,
        onBeforeRequest: async (req) => {
          // getValidIdentityToken already reinitializes the SDK once. Reject here so
          // the chunk is not sent unsigned; tus retries this callback, then fails the file.
          const identityToken = await getValidIdentityToken(ucsSdkRef, reinitializeSdk);
          if (!identityToken) {
            throw new Error('Missing UCS identity token');
          }
          req.setHeader('Authorization', `Bearer ${identityToken}`);
        },
        chunkSize: TUS_CHUNK_SIZE,
      })
      .on('upload', onUploadStart)
      .on('upload-progress', onUploadProgress)
      .on('cancel-all', onCancelAll)
      .on('upload-error', handleError)
      .on('transloadit:complete', onTransloaditComplete)
      .on('complete', onComplete)
      .use(Dashboard, dashboardOptions)
      .use(ImageEditor, {
        target: DASHBOARD_TARGET,
        ...(cropperOptions && { cropperOptions }),
      })
      .use(FileSearchPlugin, {
        id: 'FileSearchPlugin',
        target: null,
      })
      .use(Box, {
        companionUrl: COMPANION_SANDBOX_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        target: DASHBOARD_TARGET,
      })
      .use(Url, {
        companionUrl: COMPANION_SANDBOX_URL,
        target: DASHBOARD_TARGET,
      })
      .use(Dropbox, {
        companionUrl: COMPANION_SANDBOX_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        target: DASHBOARD_TARGET,
      })
      .use(GoogleDrive, {
        companionUrl: COMPANION_SANDBOX_URL,
        companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
        target: DASHBOARD_TARGET,
      })
      .use(DragDrop, {
        target: trigger,
      });

    const targetElement = document.querySelector(trigger);
    const openDashboard = (event: Event): void => {
      event.preventDefault();
      const dashboard = uppy.getPlugin<Dashboard<Meta, Body>>('Dashboard');
      if (dashboard) {
        dashboard.openModal();
      }
    };
    if (targetElement) {
      targetElement.addEventListener('click', openDashboard);
    }

    function updateDashboardClass(): void {
      const dashboardEl = document.querySelector('.uppy-Dashboard-files');
      if (!dashboardEl) return;
      const items = dashboardEl.querySelectorAll('.uppy-Dashboard-Item');
      dashboardEl.classList.toggle('single-item', items.length === 1);
    }

    // Files that fit in a single chunk gain nothing from parallel streams and would otherwise trigger
    // multiple signed-URL fetches, so pin them to one. This must run at upload time: FileSearchPlugin
    // replaces file state with its `file-added` snapshots when `upload()` is called.
    const pinSingleChunkFiles = (fileIDs: string[]): void => {
      fileIDs.forEach((fileID) => {
        const file = uppy.getFile(fileID);
        if (file?.size != null && file.size <= TUS_CHUNK_SIZE) {
          uppy.setFileState(fileID, {
            tus: {
              ...(file as unknown as { tus?: Record<string, unknown> }).tus,
              parallelUploads: 1,
            },
          } as Parameters<typeof uppy.setFileState>[1]);
        }
      });
    };
    uppy.addPreProcessor(pinSingleChunkFiles);

    const onFileAdded = (): void => {
      // Delay to give Uppy time to render DOM for new file
      requestAnimationFrame(updateDashboardClass);

      // Automatically open the Dashboard modal when files are added
      const dashboard = uppy.getPlugin<Dashboard<Meta, Body>>('Dashboard');
      if (dashboard && !dashboard.isModalOpen()) {
        dashboard.openModal();
      }
    };

    const onFileRemoved = (): void => {
      requestAnimationFrame(updateDashboardClass);
    };

    // `resumeAll()` only resumes files that had already started, so files added while paused would otherwise wait
    // for the status bar's small "Upload" link.
    const onResumeAll = (): void => {
      if (getUnqueuedFileIDs(uppy).length === 0) return;
      uppy.upload().catch(() => {
        // Reported through Uppy's `error` event
      });
    };

    uppy.on('file-added', onFileAdded);
    uppy.on('file-removed', onFileRemoved);
    uppy.on('resume-all', onResumeAll);

    // Observe DOM until dashboard is mounted, then run initial update
    const observer = new MutationObserver(() => {
      const dashboardEl = document.querySelector('.uppy-Dashboard-files');
      if (dashboardEl) {
        updateDashboardClass();
        observer.disconnect(); // Stop observing once found
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      uppy.off('file-added', onFileAdded);
      uppy.off('file-removed', onFileRemoved);
      uppy.off('resume-all', onResumeAll);
      uppy.removePreProcessor(pinSingleChunkFiles);
      observer.disconnect();
      targetElement?.removeEventListener('click', openDashboard);
      // The Dashboard mounts into `document.body`, so without destroy() the old
      // dashboard (and its file list) stays in the DOM after this component
      // unmounts and reappears alongside the next one on remount.
      destroyUppy();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDestroyedRef.current) return;
    const dashboard = uppy.getPlugin<Dashboard<Meta, Body>>('Dashboard');
    if (dashboard && !inline) {
      // Only manually render for modal dashboards, inline dashboards render automatically.
      // Kept in an effect so it cannot run during render (or after teardown).
      dashboard.render(uppy.getState());
    }
  });

  return (inline || !trigger) ? (
    <div id={`${uniqIdentifier}-dashboard`} />
  ) : null;
};
